import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight, CheckCircle, CircleNotch,
  Rocket, Target, Lightbulb, Trophy, Star, Heart, Sparkle, Lightning,
  ShieldCheck, Users, ChartBar, ChartLine, Robot, Brain, Cpu, Cloud,
  Database, Code, Globe, EnvelopeSimple, Phone, Calendar, Clock, Gift,
  Handshake, Gauge, Graph, Coins, MagnifyingGlass,
} from "@phosphor-icons/react";

// Media Phase 1 — curated Phosphor set mirroring backend LP_ALLOWED_ICONS.
const LP_MEDIA_ICONS = {
  Rocket, Target, Lightbulb, CheckCircle, Trophy, Star, Heart, Sparkle,
  Lightning, ShieldCheck, Users, ChartBar, ChartLine, Robot, Brain, Cpu,
  Cloud, Database, Code, Globe, EnvelopeSimple, Phone, Calendar, Clock,
  Gift, Handshake, Gauge, Graph, Coins, MagnifyingGlass,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg: "#0C0D0F",
  surface: "#17181A",
  surfaceRaised: "#1D1F22",
  textPrimary: "#F8F8F8",
  textMuted: "#9A9A9A",
  textAccent: "#A8C9CD",
  accentMid: "#6BA6AF",
  accentDeep: "#4E8690",
  hairline: "rgba(248,248,248,0.12)",
};

const FONT_STACK = 'Urbanist, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif';
const FONT_ARAPEY = 'Arapey, "Times New Roman", serif';

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X",
                "XI", "XII", "XIII", "XIV", "XV", "XVI", "XVII", "XVIII", "XIX", "XX"];

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, page: null, error: null });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/landing-pages/${slug}`)
      .then(r => { if (!cancelled) setState({ loading: false, page: r.data, error: null }); })
      .catch(err => {
        if (!cancelled) setState({
          loading: false, page: null,
          error: err?.response?.status === 404 ? "not_found" : "network",
        });
      });
    return () => { cancelled = true; };
  }, [slug]);

  // GA4 auto-inject — guarded so it is a no-op when no website/measurement_id
  // is linked and survives React StrictMode double-mount via a global flag.
  const ga4MID = state.page?.ga4_measurement_id || null;
  useEffect(() => {
    if (!ga4MID) return;
    if (typeof window === "undefined") return;
    if (window.__ga4_loaded === ga4MID) return;  // already injected for this MID
    // If a different MID was previously injected in this SPA session, we
    // still respect the newest one — gtag can hold multiple configs.
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4MID)}`;
    s.setAttribute("data-ga4", ga4MID);
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", ga4MID, { send_page_view: true });
    window.__ga4_loaded = ga4MID;
  }, [ga4MID]);

  const shell = (children, testid) => (
    <div style={{ background: T.bg, color: T.textPrimary, fontFamily: FONT_STACK, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }} data-testid={testid}>
      <FontLoader />{children}
    </div>
  );

  if (state.loading) return shell(<div style={{ color: T.textMuted, fontSize: 14 }}>Loading…</div>, "public-lp-loading");
  if (state.error === "not_found") return shell(
    <div style={{ maxWidth: 480, textAlign: "center", padding: "40px 32px", border: `1px solid ${T.hairline}` }}>
      <div style={{ fontSize: 56, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1 }}>Not found</div>
      <p style={{ marginTop: 20, color: T.textMuted, fontSize: 15 }}>This page doesn't exist or is no longer available.</p>
    </div>, "public-lp-notfound");
  if (state.error) return shell(
    <div style={{ maxWidth: 480, textAlign: "center", padding: "40px 32px", border: `1px solid ${T.hairline}` }}>
      <div style={{ fontSize: 56, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1 }}>Couldn't load</div>
      <p style={{ marginTop: 20, color: T.textMuted, fontSize: 15 }}>Please refresh & try again.</p>
    </div>);

  return <B2BTemplate page={state.page} slug={slug} />;
}

function FontLoader() {
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;500;600;700&family=Arapey:ital@1&display=swap" />
    </>
  );
}

function B2BTemplate({ page: p, slug }) {
  const [values, setValues] = useState({ name: "", email: "", phone: "", company: "", hp_website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const fields = p.form.fields;
  const formRef = useRef(null);

  const revealMode = p.form_reveal_mode || "immediate";
  const heroCtaDefault = p.hero_cta_default || "See How AI Could Improve Your Business";

  // GA4 helper — no-op when gtag isn't configured. Every event call is guarded.
  const ga4MID = p.ga4_measurement_id || null;
  const gaTrack = (name, params) => {
    try {
      if (!ga4MID) return;
      if (typeof window === "undefined" || typeof window.gtag !== "function") return;
      window.gtag("event", name, { send_to: ga4MID, page_slug: slug, ...(params || {}) });
    } catch { /* silent — never block UX on tracking */ }
  };

  // reveal state — "hidden" / "assessment" / "form"
  const initialReveal = revealMode === "immediate" ? "form" : "hidden";
  const [revealStage, setRevealStage] = useState(initialReveal);
  const [assessment, setAssessment] = useState({
    questions: [], answers: [], step: 0, running: false,
    evaluation: null, token: null, error: null, stepError: null,
  });

  const scrollForm = () => {
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const startReveal = async () => {
    gaTrack("cta_click", { reveal_mode: revealMode });
    if (revealMode === "on_cta") {
      setRevealStage("form");
      scrollForm();
      return;
    }
    // ai_assessment — load questions (may be page-custom or the default 4)
    try {
      const { data } = await axios.get(`${BACKEND}/api/public/landing-pages/${slug}/assessment-config`);
      setAssessment(a => ({ ...a, questions: data.questions,
                             answers: data.questions.map(() => ""), step: 0,
                             evaluation: null, token: null, error: null, stepError: null }));
      setRevealStage("assessment");
      gaTrack("assessment_start", { question_count: data.questions?.length || 0 });
      scrollForm();
    } catch {
      setRevealStage("form");
      scrollForm();
    }
  };

  const setAnswer = (idx, v) => {
    setAssessment(a => {
      const answers = [...a.answers];
      answers[idx] = v;
      // Typing clears any prior step error inline.
      return { ...a, answers, stepError: null };
    });
  };

  // BUG FIX — was dispatching a CustomEvent nobody listened to. Now advances
  // step directly via state, with inline validation feedback instead of alert().
  const advanceStep = () => {
    setAssessment(a => {
      const v = (a.answers[a.step] || "").trim();
      if (!v) return { ...a, stepError: "Please enter an answer to continue." };
      const answers = [...a.answers];
      answers[a.step] = v; // trimmed
      return { ...a, answers, step: a.step + 1, stepError: null };
    });
  };

  // Assessment abandon — fires ONCE if the user started the assessment
  // (revealStage moved to "assessment"), didn't complete it (no evaluation),
  // didn't submit the form (not submitted), and either hides the tab or
  // unloads. Never double-fires; also no-op if GA4 isn't configured.
  useEffect(() => {
    if (revealStage !== "assessment") return;
    let fired = false;
    const fire = () => {
      if (fired) return;
      if (assessment.evaluation || submitted) return;
      fired = true;
      gaTrack("assessment_abandon", { step: assessment.step,
                                       total: assessment.questions.length });
    };
    const onVis = () => { if (document.visibilityState === "hidden") fire(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("beforeunload", fire);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeunload", fire);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealStage, assessment.evaluation, submitted, assessment.step, assessment.questions.length]);

  const submitAssessment = async () => {
    setAssessment(a => ({ ...a, running: true, error: null, stepError: null }));
    try {
      const { data } = await axios.post(
        `${BACKEND}/api/public/landing-pages/${slug}/assessment`,
        { answers: assessment.answers });
      setAssessment(a => ({ ...a, running: false,
                             evaluation: data.evaluation, token: data.assessment_token }));
      gaTrack("assessment_complete", { has_evaluation: !!data.evaluation });
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many attempts — please try again in a minute."
        : (err?.response?.data?.detail || "Assessment failed. Try again.");
      setAssessment(a => ({ ...a, running: false, error: msg }));
    }
  };

  const orderedEnabled = (p.sections || [])
    .filter(s => s && s.enabled && s.content)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const hero = orderedEnabled.find(s => s.section_type === "hero");
  const nonHeroContent = orderedEnabled.filter(s => s.section_type !== "hero");

  const onChange = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!values.name.trim()) { setSubmitError("Please enter your name"); return; }
    setSubmitting(true);
    try {
      const payload = { ...values };
      if (assessment.token) payload.assessment_token = assessment.token;
      payload.landing_page_slug = slug;
      await axios.post(`${BACKEND}/api/public/forms/${p.form.public_slug}/submit`, payload);
      setSubmitted(true);
      gaTrack("lead_submit", { with_assessment: !!assessment.token });
    } catch (err) {
      if (err?.response?.status === 429) setSubmitError("Too many submissions from this address. Please try again in a minute.");
      else setSubmitError(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  const isMinimal = orderedEnabled.length === 0;
  const showFormColumn = revealStage === "form" || (revealMode === "immediate");
  const showAssessmentPanel = revealStage === "assessment";

  // ── Theming Layer — build CSS var overrides + optional Google font link.
  const theme = p.theme || null;
  const fontPresets = p.font_presets || {};
  const fontPair = theme?.font_choice ? fontPresets[theme.font_choice] : null;
  const themeVars = {};
  if (theme?.primary_color)    themeVars["--lp-primary"]    = theme.primary_color;
  if (theme?.secondary_color)  themeVars["--lp-secondary"]  = theme.secondary_color;
  if (theme?.accent_color)     themeVars["--lp-accent"]     = theme.accent_color;
  if (theme?.background_color) themeVars["--lp-bg"]         = theme.background_color;
  if (fontPair) {
    themeVars["--lp-font-heading"] = `'${fontPair.heading}', ${FONT_STACK}`;
    themeVars["--lp-font-body"]    = `'${fontPair.body}', ${FONT_STACK}`;
    themeVars["--lp-font-italic"]  = `'${fontPair.italic}', ${FONT_ARAPEY}`;
  }
  if (theme?.button_style) {
    themeVars["--lp-btn-radius"] =
      theme.button_style === "pill"    ? "999px" :
      theme.button_style === "square"  ? "0px"   : "8px";
  }
  if (theme?.border_radius) {
    themeVars["--lp-card-radius"] =
      theme.border_radius === "none"   ? "0px"  :
      theme.border_radius === "small"  ? "4px"  :
      theme.border_radius === "medium" ? "8px"  : "16px";
  }
  if (theme?.spacing_density) {
    themeVars["--lp-space-scale"] =
      theme.spacing_density === "compact"    ? "0.75" :
      theme.spacing_density === "spacious"   ? "1.35" : "1";
  }

  // Build a single Google Fonts link when a preset is chosen. `system` still
  // uses the existing Urbanist/Arapey which is loaded by <FontLoader/> above.
  const fontFamiliesForGoogle = fontPair
    ? Array.from(new Set([fontPair.heading, fontPair.body, fontPair.italic]))
        .filter(f => !["Urbanist", "Arapey"].includes(f))
    : [];

  return (
    <div
      data-testid="public-lp-page"
      data-template="b2b"
      data-enabled-sections={orderedEnabled.length}
      data-reveal-mode={revealMode}
      data-reveal-stage={revealStage}
      data-form-revealed={showFormColumn ? "true" : "false"}
      data-theme-applied={theme ? "true" : "false"}
      style={{ background: "var(--lp-bg, " + T.bg + ")", color: T.textPrimary, fontFamily: "var(--lp-font-body, " + FONT_STACK + ")", minHeight: "100vh", position: "relative", overflow: "hidden", ...themeVars }}
    >
      <FontLoader />
      {fontFamiliesForGoogle.length > 0 && (
        <link data-testid="public-lp-theme-fonts" rel="stylesheet"
              href={`https://fonts.googleapis.com/css2?${fontFamiliesForGoogle.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700`).join("&")}&display=swap`} />
      )}
      {theme?.logo_url && (
        <img data-testid="public-lp-theme-logo"
             src={theme.logo_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
             style={{ position: "absolute", top: 20, left: 24, height: 32, width: "auto", zIndex: 2 }} />
      )}
      <style>{`
        .lp-h1, .lp-h2, .lp-cta-headline { font-family: var(--lp-font-heading, ${FONT_STACK}) !important; }
        .lp-italic { font-family: var(--lp-font-italic, ${FONT_ARAPEY}) !important; }
        .lp-submit { border-radius: var(--lp-btn-radius, 999px) !important;
                     background: var(--lp-primary, ${T.surface}) !important; }
        .lp-form-wrap { border-radius: var(--lp-card-radius, 0px) !important; }
        .lp-proof-metric, .lp-numeral, .lp-attrib strong { color: var(--lp-accent, ${T.textAccent}); }
        .lp-section { margin-top: calc(72px * var(--lp-space-scale, 1)); }
        .lp-section-accent { margin-top: calc(72px * var(--lp-space-scale, 1)); }
        .lp-section-outer { margin-top: calc(72px * var(--lp-space-scale, 1)); }
        .lp-section-outer > .lp-section { margin-top: 0; }
        .lp-section-accent .lp-section { margin-top: 0; }
        .lp-row       { padding: calc(20px * var(--lp-space-scale, 1)) 0; }
        .lp-testimonial { padding: calc(32px * var(--lp-space-scale, 1)) 0; }
        main.lp-main { padding-top: calc(88px * var(--lp-space-scale, 1)) !important; }
      `}</style>
      <style>{`
        .lp-root, .lp-root * { box-shadow: none !important; }
        .lp-root { font-family: ${FONT_STACK}; letter-spacing: -0.005em; }
        .lp-h1 { font-family: ${FONT_STACK}; font-weight: 400; letter-spacing: -0.025em; line-height: 1.02; }
        .lp-h2 { font-family: ${FONT_STACK}; font-weight: 400; letter-spacing: -0.02em; line-height: 1.08; font-size: 40px; color: ${T.textPrimary}; margin: 0; }
        .lp-italic { font-family: ${FONT_ARAPEY}; font-style: italic; font-weight: 400; }
        .lp-eyebrow { font-size: 13px; letter-spacing: 0.02em; color: ${T.textMuted}; font-weight: 400; }
        .lp-numeral { font-family: ${FONT_ARAPEY}; font-style: italic; font-weight: 400; color: ${T.textAccent}; font-size: 14px; letter-spacing: 0.02em; }
        .lp-input { width: 100%; height: 44px; background: transparent; border: none; border-bottom: 1px solid ${T.hairline}; color: ${T.textPrimary}; font-size: 15px; font-family: ${FONT_STACK}; padding: 0 2px; outline: none; transition: border-color .25s ease; }
        .lp-input::placeholder { color: ${T.textMuted}; opacity: 0.7; }
        .lp-input:focus { border-bottom-color: ${T.textAccent}; }
        .lp-textarea { width: 100%; min-height: 120px; background: transparent; border: 1px solid ${T.hairline}; color: ${T.textPrimary}; font-size: 15px; font-family: ${FONT_STACK}; padding: 12px 14px; outline: none; resize: vertical; transition: border-color .25s ease; }
        .lp-textarea:focus { border-color: ${T.textAccent}; }
        .lp-submit { display: inline-flex; align-items: center; justify-content: center; gap: 10px; height: 48px; padding: 0 26px; border-radius: 999px; background: ${T.surface}; color: ${T.textPrimary}; border: 1px solid ${T.hairline}; font-family: ${FONT_STACK}; font-size: 15px; font-weight: 500; cursor: pointer; transition: background-color .25s ease, border-color .25s ease; }
        .lp-submit:hover { background: ${T.surfaceRaised}; border-color: ${T.textAccent}; }
        .lp-submit:disabled { opacity: 0.55; cursor: not-allowed; }
        .lp-form-wrap { background: ${T.surface}; border: 1px solid ${T.hairline}; }
        .lp-row { padding: 20px 0; border-top: 1px solid ${T.hairline}; font-size: 16px; color: ${T.textPrimary}; font-weight: 400; line-height: 1.5; }
        .lp-row:last-child { border-bottom: 1px solid ${T.hairline}; }
        .lp-testimonial { padding: 32px 0; border-top: 1px solid ${T.hairline}; }
        .lp-testimonial:last-child { border-bottom: 1px solid ${T.hairline}; }
        .lp-quote { font-size: 17px; color: ${T.textPrimary}; font-weight: 400; line-height: 1.55; letter-spacing: -0.005em; }
        .lp-attrib { margin-top: 16px; font-size: 13px; color: ${T.textMuted}; font-weight: 400; }
        .lp-attrib strong { color: ${T.textPrimary}; font-weight: 500; }
        .lp-two-col { display: grid; grid-template-columns: 1fr; gap: 56px; }
        .lp-section { margin-top: 72px; }
        .lp-section-accent { margin-top: 72px; }
        .lp-section-outer { margin-top: 72px; }
        .lp-section-outer > .lp-section { margin-top: 0; }
        .lp-section:first-of-type { margin-top: 0; }
        .lp-section-outer:first-of-type { margin-top: 0; }
        .lp-section-head { display: flex; align-items: baseline; gap: 12px; margin-bottom: 24px; }
        .lp-body-para { font-size: 16px; color: ${T.textMuted}; line-height: 1.7; max-width: 620px; white-space: pre-wrap; }
        .lp-proof-metric { display: inline-block; margin-top: 12px; padding: 6px 12px; border: 1px solid ${T.hairline}; color: ${T.textAccent}; font-size: 13px; letter-spacing: 0.02em; }
        .lp-faq-q { font-size: 16px; color: ${T.textPrimary}; font-weight: 500; }
        .lp-faq-a { margin-top: 8px; font-size: 15px; color: ${T.textMuted}; line-height: 1.6; white-space: pre-wrap; }
        .lp-cta-headline { font-family: ${FONT_STACK}; font-weight: 400; letter-spacing: -0.02em; line-height: 1.08; font-size: 44px; color: ${T.textPrimary}; margin: 0; }
        .lp-section-media-2col { display: block; }
        @media (min-width: 1024px) {
          .lp-two-col { grid-template-columns: 1.2fr 1fr; gap: 88px; align-items: start; }
          .lp-form-col { position: sticky; top: 56px; }
          .lp-section-media-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: center; }
          .lp-section-accent { margin-top: 72px; }
        }
      `}</style>

      <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-30%", right: "-15%", width: "70vw", height: "70vw", maxWidth: 900, maxHeight: 900, background: `radial-gradient(closest-side, ${T.accentDeep}22, transparent 70%)` }} />
        <div style={{ position: "absolute", top: "20%", left: "-20%", width: "60vw", height: "60vw", maxWidth: 700, maxHeight: 700, background: `radial-gradient(closest-side, ${T.accentMid}14, transparent 70%)` }} />
      </div>

      <div className="lp-root" style={{ position: "relative", zIndex: 1 }}>
        <header style={{ borderBottom: `1px solid ${T.hairline}` }}>
          <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 15, fontWeight: 500, letterSpacing: "-0.01em" }}>ApexGrey</div>
            <div className="lp-eyebrow" style={{ fontSize: 13, color: T.textMuted }}>A studio for teams that ship</div>
          </div>
        </header>

        <main style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 24px 64px" }}>
          <div className="lp-two-col" data-single-column={revealStage === "hidden" && revealMode !== "immediate" ? "true" : "false"}>
            <section>
      {hero && <HeroSection s={hero} revealMode={revealMode} revealStage={revealStage}
                                     onReveal={startReveal} defaultCta={heroCtaDefault} />}
              {isMinimal && (
                <div data-testid="public-lp-minimal" style={{ marginTop: 32 }}>
                  <h1 className="lp-h1" style={{ fontSize: "clamp(56px, 7.4vw, 76px)", color: T.textPrimary, margin: 0 }}>
                    {p.name || "Get in touch"}
                  </h1>
                  <p style={{ marginTop: 24, fontSize: 15, color: T.textMuted, fontWeight: 400, lineHeight: 1.7, maxWidth: 620 }}>Use the form to reach us.</p>
                </div>
              )}
              {nonHeroContent.map((s, idx) => (
                <RenderSection key={s.section_id || `${s.section_type}-${idx}`}
                  section={s} numeral={ROMAN[idx] || String(idx + 1)} onCtaClick={startReveal} />
              ))}
            </section>

            {(showFormColumn || showAssessmentPanel) && (
              <aside data-testid="public-lp-form-column">
                <div className="lp-form-col" ref={formRef}>
                  {showAssessmentPanel && !submitted && (
                    <AssessmentPanel
                      state={assessment}
                      setAnswer={setAnswer}
                      onAdvance={advanceStep}
                      onSubmitAssessment={submitAssessment}
                      onProceedToForm={() => setRevealStage("form")}
                    />
                  )}
                  {showFormColumn && !submitted && (
                    <FormPanel
                      p={p} fields={fields} values={values}
                      submitting={submitting} submitError={submitError}
                      onChange={onChange} onSubmit={submit}
                      priorEvaluation={assessment.evaluation}
                    />
                  )}
                  {submitted && (
                    <div className="lp-form-wrap" style={{ padding: 40 }} data-testid="public-lp-success">
                      <div className="lp-eyebrow">Received</div>
                      <div style={{ marginTop: 20, fontSize: 56, fontWeight: 400, letterSpacing: "-0.025em", lineHeight: 1, color: T.textPrimary }}>Thank you</div>
                      <p style={{ marginTop: 24, fontSize: 15, color: T.textMuted, lineHeight: 1.6 }}>We received your submission & will be in touch shortly.</p>
                    </div>
                  )}
                </div>
              </aside>
            )}
          </div>
        </main>

        <footer style={{ borderTop: `1px solid ${T.hairline}` }}>
          <div style={{ maxWidth: 1120, margin: "0 auto", padding: "56px 24px 72px" }}>
            <p data-testid="public-lp-coda" className="lp-italic" style={{ margin: 0, fontSize: 18, color: T.textAccent, letterSpacing: "-0.005em" }}>Considered work, quietly delivered.</p>
            <p style={{ marginTop: 40, fontSize: 13, color: T.textMuted, fontWeight: 400 }}>Powered by ApexGrey</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

function HeroSection({ s, revealMode, revealStage, onReveal, defaultCta }) {
  const c = s.content || {};
  if (!c.headline && !c.subheadline) return null;
  const showCtaButton = revealMode !== "immediate" && revealStage === "hidden";
  const ctaLabel = (c.cta_text || "").trim() || defaultCta;
  const variant = s.variant || "split";
  const isCentered = variant === "centered";
  const isStatLed = variant === "stat_led";
  const heroInner = (
    <>
      {isStatLed && c.stat_value && (
        <div data-testid="public-lp-hero-stat" style={{ marginBottom: 24 }}>
          <div className="lp-h1" style={{ fontSize: "clamp(72px, 9vw, 112px)", color: "var(--lp-accent, " + T.textAccent + ")", margin: 0, lineHeight: 1 }}
               data-testid="public-lp-hero-stat-value">{c.stat_value}</div>
          {c.stat_label && <div className="lp-eyebrow" style={{ marginTop: 8, fontSize: 14 }}
                                data-testid="public-lp-hero-stat-label">{c.stat_label}</div>}
        </div>
      )}
      {c.headline && (
        <h1 data-testid="public-lp-headline" className="lp-h1"
          style={{ fontSize: "clamp(56px, 7.4vw, 76px)", color: T.textPrimary, margin: 0 }}>{c.headline}</h1>
      )}
      {c.subheadline && (
        <p data-testid="public-lp-subheadline"
          style={{ marginTop: 32, fontSize: 18, color: T.textMuted, fontWeight: 400, lineHeight: 1.55,
                    maxWidth: isCentered ? "none" : 620, marginInline: isCentered ? "auto" : undefined }}>{c.subheadline}</p>
      )}
      {showCtaButton && (
        <div style={{ marginTop: 40 }}>
          <button type="button" data-testid="public-lp-hero-cta" onClick={onReveal} className="lp-submit">
            <span>{ctaLabel}</span>
            <ArrowUpRight weight="regular" size={16} color={T.textAccent} />
          </button>
        </div>
      )}
    </>
  );
  return (
    <div data-testid="public-lp-section-hero" data-variant={variant}
         style={isCentered ? { textAlign: "center", maxWidth: 820, marginInline: "auto" } : {}}>
      <SectionMediaWrapper section={s}>{heroInner}</SectionMediaWrapper>
    </div>
  );
}

function AssessmentPanel({ state, setAnswer, onAdvance, onSubmitAssessment, onProceedToForm }) {
  const total = state.questions.length;
  const step = state.step;
  const isLastQuestion = step === total - 1;
  const answering = !state.evaluation && !state.running;

  // Analyzing state
  if (state.running) {
    return (
      <div className="lp-form-wrap" style={{ padding: 40 }} data-testid="public-lp-assessment-analyzing">
        <div className="lp-eyebrow">One moment</div>
        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12, fontSize: 20, color: T.textPrimary }}>
          <CircleNotch weight="bold" size={20} color={T.textAccent} style={{ animation: "spin 1s linear infinite" }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Analyzing your responses…
        </div>
        <p style={{ marginTop: 20, fontSize: 14, color: T.textMuted }}>Grounded strictly in what you told us — no fabricated numbers.</p>
      </div>
    );
  }

  // Evaluation shown; user proceeds to form
  if (state.evaluation) {
    return (
      <div className="lp-form-wrap" style={{ padding: 40 }} data-testid="public-lp-assessment-evaluation">
        <div className="lp-eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle weight="duotone" size={14} color={T.textAccent} />
          Your quick evaluation
        </div>
        <p style={{ marginTop: 24, fontSize: 16, color: T.textPrimary, lineHeight: 1.7, whiteSpace: "pre-wrap" }}
           data-testid="public-lp-assessment-evaluation-text">
          {state.evaluation}
        </p>
        <div style={{ marginTop: 32 }}>
          <button type="button" data-testid="public-lp-assessment-continue" onClick={onProceedToForm} className="lp-submit">
            <span>Continue to contact form</span>
            <ArrowUpRight weight="regular" size={16} color={T.textAccent} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lp-form-wrap" style={{ padding: 40 }} data-testid="public-lp-assessment-panel"
         data-assessment-step={step}>
      <div className="lp-eyebrow" data-testid="public-lp-assessment-progress">
        Question {step + 1} of {total}
      </div>
      <div style={{ marginTop: 12, height: 2, background: T.hairline, position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${((step + 1) / total) * 100}%`, background: T.textAccent, transition: "width .3s ease" }} />
      </div>
      <div style={{ marginTop: 32, fontSize: 22, color: T.textPrimary, fontWeight: 400, letterSpacing: "-0.01em", lineHeight: 1.3 }}
           data-testid={`public-lp-assessment-question-${step}`}>
        {state.questions[step]}
      </div>
      <textarea
        data-testid={`public-lp-assessment-answer-${step}`}
        className="lp-textarea"
        style={{ marginTop: 24 }}
        value={state.answers[step] || ""}
        onChange={(e) => setAnswer(step, e.target.value)}
        maxLength={1000}
        placeholder="Type your answer…"
      />
      {state.stepError && (
        <div data-testid="public-lp-assessment-step-error"
             style={{ marginTop: 12, fontSize: 13, color: T.textAccent }}>
          {state.stepError}
        </div>
      )}
      {state.error && (
        <div data-testid="public-lp-assessment-error"
             style={{ marginTop: 16, padding: "10px 12px", border: `1px solid ${T.hairline}`, fontSize: 13, color: T.textPrimary }}>
          {state.error}
        </div>
      )}
      <div style={{ marginTop: 32, display: "flex", justifyContent: "flex-end", gap: 12 }}>
        {!isLastQuestion ? (
          <button type="button"
                  data-testid="public-lp-assessment-next"
                  onClick={onAdvance}
                  className="lp-submit">
            <span>Next</span>
            <ArrowUpRight weight="regular" size={16} color={T.textAccent} />
          </button>
        ) : (
          <button type="button"
                  data-testid="public-lp-assessment-submit"
                  onClick={onSubmitAssessment}
                  className="lp-submit"
                  disabled={!(state.answers[step] || "").trim() || !answering}>
            <span>Analyze my responses</span>
            <ArrowUpRight weight="regular" size={16} color={T.textAccent} />
          </button>
        )}
      </div>
    </div>
  );
}

function FormPanel({ p, fields, values, submitting, submitError, onChange, onSubmit, priorEvaluation }) {
  return (
    <form onSubmit={onSubmit} className="lp-form-wrap" style={{ padding: 40 }} data-testid="public-lp-form">
      <div className="lp-eyebrow">Book your slot</div>
      <div style={{ marginTop: 12, fontSize: 18, color: T.textPrimary, fontWeight: 400 }}>{p.form.name}</div>
      <div style={{ marginTop: 6, fontSize: 13, color: T.textMuted }}>Takes under a minute. No credit card required.</div>
      {priorEvaluation && (
        <div data-testid="public-lp-form-evaluation-echo"
              style={{ marginTop: 20, padding: "12px 14px", border: `1px solid ${T.hairline}`, fontSize: 13, color: T.textMuted, lineHeight: 1.5 }}>
          Your responses have been attached to this submission.
        </div>
      )}
      <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <div className="lp-eyebrow" style={{ marginBottom: 4, fontSize: 13 }}>Name *</div>
          <input data-testid="public-lp-name" className="lp-input" value={values.name}
                  onChange={e => onChange("name", e.target.value)} maxLength={fields.name.max_length} required />
        </div>
        {fields.email.shown && (
          <div>
            <div className="lp-eyebrow" style={{ marginBottom: 4, fontSize: 13 }}>Email {fields.email.required && "*"}</div>
            <input data-testid="public-lp-email" className="lp-input" type="email" value={values.email}
                    onChange={e => onChange("email", e.target.value)}
                    maxLength={fields.email.max_length} required={fields.email.required} />
          </div>
        )}
        {fields.phone.shown && (
          <div>
            <div className="lp-eyebrow" style={{ marginBottom: 4, fontSize: 13 }}>Phone {fields.phone.required && "*"}</div>
            <input data-testid="public-lp-phone" className="lp-input" value={values.phone}
                    onChange={e => onChange("phone", e.target.value)}
                    maxLength={fields.phone.max_length} required={fields.phone.required} />
          </div>
        )}
        {fields.company.shown && (
          <div>
            <div className="lp-eyebrow" style={{ marginBottom: 4, fontSize: 13 }}>Company {fields.company.required && "*"}</div>
            <input data-testid="public-lp-company" className="lp-input" value={values.company}
                    onChange={e => onChange("company", e.target.value)}
                    maxLength={fields.company.max_length} required={fields.company.required} />
          </div>
        )}
      </div>
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label>Website (leave empty)</label>
        <input data-testid="public-lp-honeypot" tabIndex={-1} autoComplete="off"
                value={values.hp_website} onChange={e => onChange("hp_website", e.target.value)} />
      </div>
      {submitError && (
        <div data-testid="public-lp-error"
              style={{ marginTop: 24, padding: "12px 14px", border: `1px solid ${T.hairline}`, fontSize: 13, color: T.textPrimary }}>
          {submitError}
        </div>
      )}
      <div style={{ marginTop: 40 }}>
        <button type="submit" data-testid="public-lp-submit" className="lp-submit" disabled={submitting}>
          <span>{submitting ? "Submitting…" : "Get started"}</span>
          {!submitting && <ArrowUpRight weight="regular" size={16} color={T.textAccent} />}
        </button>
      </div>
      <div style={{ marginTop: 28, fontSize: 13, color: T.textMuted }}>Your data stays private.</div>
    </form>
  );
}

function SectionHead({ numeral, eyebrow, heading }) {
  return (
    <div className="lp-section-head" data-testid={`public-lp-section-head-${(eyebrow || "").toLowerCase()}`}>
      <span className="lp-numeral">{numeral}</span>
      <span className="lp-eyebrow">{eyebrow}</span>
      {heading && <span style={{ color: T.textMuted, fontSize: 13 }}>· {heading}</span>}
    </div>
  );
}

function RenderSection({ section: s, numeral, onCtaClick }) {
  const inner = <RenderSectionInner section={s} numeral={numeral} onCtaClick={onCtaClick} />;
  return <SectionMediaWrapper section={s}>{inner}</SectionMediaWrapper>;
}

// ── Media Phase 1 — public renderer ─────────────────────────────────────
// Wraps a section's content and layouts optional `media` per placement +
// display_style. When `section.media` is null (default), children render
// exactly as before. Left/right placements stack vertically on mobile.
function SectionMediaWrapper({ section, children }) {
  const m = section.media;
  if (!m) return children;
  // Icons are lightweight ACCENTS — never a hero visual. They route through
  // a compact inline layout that sits close to the section title instead of
  // the 2-col grid used by images/video/logo_strip/before_after.
  if (m.type === "icon" || m.type === "icon_group") {
    return <SectionIconAccent media={m}>{children}</SectionIconAccent>;
  }
  const media = <SectionMedia media={m} />;
  const placement = m.placement || "below";
  if (placement === "left" || placement === "right") {
    return (
      <div className="lp-section-outer lp-section-media-2col"
           data-testid={`public-lp-section-media-${placement}`}
           data-media-placement={placement}>
        {placement === "left" ? <>{media}{children}</> : <>{children}{media}</>}
      </div>
    );
  }
  if (placement === "center") {
    return (
      <div className="lp-section-outer" data-testid="public-lp-section-media-center" data-media-placement="center">
        {media}
        {children}
      </div>
    );
  }
  return (
    <div className="lp-section-outer" data-testid="public-lp-section-media-below" data-media-placement="below">
      {children}
      {media}
    </div>
  );
}

// Compact icon accent — small icon (or tight cluster) sitting near the
// section title/eyebrow, respecting placement without dominating the layout.
function SectionIconAccent({ media, children }) {
  const icons = <CompactSectionIcons media={media} />;
  const placement = media.placement || "below";
  const testid = `public-lp-section-media-${placement}`;
  // The accent wrapper OWNS the section-to-section spacing (via .lp-section-accent).
  // The inner .lp-section's own margin-top is killed by CSS so icons stay
  // TIGHTLY coupled to their section title — clearly one unit, not floating
  // between two sections.
  const cls = "lp-section-outer lp-section-accent";
  if (placement === "left") {
    return (
      <div data-testid={testid} data-media-placement="left" className={cls}
           style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ paddingTop: 4, flexShrink: 0 }}>{icons}</div>
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      </div>
    );
  }
  if (placement === "right") {
    return (
      <div data-testid={testid} data-media-placement="right" className={cls}
           style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        <div style={{ paddingTop: 4, flexShrink: 0 }}>{icons}</div>
      </div>
    );
  }
  if (placement === "center") {
    return (
      <div data-testid={testid} data-media-placement="center" className={cls}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>{icons}</div>
        {children}
      </div>
    );
  }
  return (
    <div data-testid={testid} data-media-placement="below" className={cls}>
      {children}
      <div style={{ marginTop: 12 }}>{icons}</div>
    </div>
  );
}

function CompactSectionIcons({ media }) {
  const style = media.display_style || "contained";
  if (media.type === "icon") {
    const Icon = LP_MEDIA_ICONS[media.source];
    if (!Icon) return null;
    return (
      <span data-testid="public-lp-media"
            data-media-type="icon"
            data-media-display-style={style}
            style={{ display: "inline-flex", alignItems: "center" }}>
        <Icon data-testid="public-lp-media-icon"
              weight="duotone" size={24}
              color="var(--lp-accent, #A8C9CD)" />
      </span>
    );
  }
  const names = Array.isArray(media.source) ? media.source : [];
  return (
    <span data-testid="public-lp-media"
          data-media-type="icon_group"
          data-media-display-style={style}
          style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
      {names.map((n, i) => {
        const Icon = LP_MEDIA_ICONS[n];
        if (!Icon) return null;
        return <Icon key={i}
                      data-testid={`public-lp-media-icon-${i}`}
                      weight="duotone" size={20}
                      color="var(--lp-accent, #A8C9CD)" />;
      })}
    </span>
  );
}

function SectionMedia({ media }) {
  const style = media.display_style || "contained";
  const isCard = style === "card";
  const wrapStyle = {
    marginTop: 24,
    padding: isCard ? 20 : 0,
    border: isCard ? `1px solid var(--lp-hairline, rgba(248,248,248,0.12))` : "none",
    borderRadius: isCard ? "var(--lp-card-radius, 0px)" : 0,
    textAlign: media.placement === "center" ? "center" : "left",
    maxWidth: style === "full_width" ? "none" : (style === "contained" ? 520 : "none"),
    marginInline: media.placement === "center" ? "auto" : undefined,
  };
  if (media.type === "image") {
    return (
      <div data-testid="public-lp-media"
           data-media-type="image"
           data-media-display-style={style}
           style={wrapStyle}>
        <img data-testid="public-lp-media-image"
             src={media.source}
             alt={media.alt_text || ""}
             loading="lazy"
             onError={(e) => { e.currentTarget.style.display = "none"; }}
             style={{ width: "100%", height: "auto", display: "block",
                      borderRadius: "var(--lp-card-radius, 0px)" }} />
      </div>
    );
  }
  if (media.type === "icon" || media.type === "icon_group") {
    // Icons never reach this branch when routed via SectionMediaWrapper,
    // but keep a safe compact fallback for direct callers.
    return <CompactSectionIcons media={media} />;
  }
  if (media.type === "video_embedded") {
    return (
      <div data-testid="public-lp-media"
           data-media-type="video_embedded"
           data-media-display-style={style}
           style={wrapStyle}>
        <VideoEmbed media={media} />
      </div>
    );
  }
  if (media.type === "logo_strip") {
    const items = Array.isArray(media.items) ? media.items : [];
    if (items.length === 0) return null;
    return (
      <div data-testid="public-lp-media"
           data-media-type="logo_strip"
           data-media-display-style={style}
           style={{ ...wrapStyle, maxWidth: "none" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 32, alignItems: "center", opacity: 0.85,
                       justifyContent: media.placement === "center" ? "center" : "flex-start" }}>
          {items.map((l, i) => (
            <img key={i} src={l.url} alt={l.name || ""}
                 data-testid={`public-lp-media-logo-${i}`}
                 loading="lazy"
                 onError={(e) => { e.currentTarget.style.display = "none"; }}
                 style={{ height: 32, width: "auto", maxWidth: 140, objectFit: "contain",
                          filter: "grayscale(1) brightness(1.3)" }} />
          ))}
        </div>
      </div>
    );
  }
  if (media.type === "before_after") {
    const items = Array.isArray(media.items) ? media.items : [];
    if (items.length !== 2) return null;
    return (
      <div data-testid="public-lp-media"
           data-media-type="before_after"
           data-media-display-style={style}
           style={{ ...wrapStyle, maxWidth: "none" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {items.map((it, i) => (
            <figure key={i} data-testid={`public-lp-media-ba-${i}`}
                     style={{ margin: 0, border: `1px solid ${i === 0 ? T.hairline : "var(--lp-accent, " + T.textAccent + ")"}`,
                              borderRadius: "var(--lp-card-radius, 0px)", overflow: "hidden" }}>
              <img src={it.url} alt={it.label || (i === 0 ? "Before" : "After")}
                   loading="lazy"
                   onError={(e) => { e.currentTarget.style.display = "none"; }}
                   style={{ width: "100%", height: "auto", display: "block" }} />
              <figcaption className="lp-eyebrow"
                           style={{ padding: "10px 14px", fontSize: 13, color: i === 0 ? T.textMuted : "var(--lp-accent, " + T.textAccent + ")" }}>
                {it.label || (i === 0 ? "Before" : "After")}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// Click-to-play video embed. Never autoplays with sound; iframe is only
// mounted after the user taps the poster, satisfying lazy-load + audio safety.
function VideoEmbed({ media }) {
  const [playing, setPlaying] = useState(false);
  const provider = media.video_provider;
  const vid = media.video_id;
  const embedUrl = provider === "youtube"
    ? `https://www.youtube.com/embed/${vid}?autoplay=1&rel=0`
    : provider === "vimeo"
      ? `https://player.vimeo.com/video/${vid}?autoplay=1`
      : "";
  const poster = media.thumbnail_url;
  if (playing && embedUrl) {
    return (
      <div data-testid="public-lp-media-video-player"
           style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "#000", borderRadius: "var(--lp-card-radius, 0px)", overflow: "hidden" }}>
        <iframe data-testid="public-lp-media-video-iframe"
                src={embedUrl}
                title={media.alt_text || "Embedded video"}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
      </div>
    );
  }
  return (
    <button type="button"
            data-testid="public-lp-media-video-poster"
            data-video-provider={provider || ""}
            onClick={() => setPlaying(true)}
            aria-label={media.alt_text || "Play video"}
            style={{ position: "relative", width: "100%", aspectRatio: "16/9",
                     background: poster ? `#000 center/cover no-repeat url(${JSON.stringify(poster)})` : "#111",
                     backgroundImage: poster ? `url(${JSON.stringify(poster)})` : "none",
                     backgroundSize: "cover", backgroundPosition: "center",
                     border: 0, padding: 0, cursor: "pointer",
                     borderRadius: "var(--lp-card-radius, 0px)", overflow: "hidden" }}>
      <span aria-hidden="true"
            style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 100%)" }} />
      <span aria-hidden="true"
            style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                     width: 72, height: 72, borderRadius: 999, background: "rgba(255,255,255,0.92)",
                     display: "flex", alignItems: "center", justifyContent: "center", color: "#111", fontSize: 24, fontWeight: 700 }}>
        <span style={{ marginLeft: 4, borderStyle: "solid", borderWidth: "10px 0 10px 16px", borderColor: "transparent transparent transparent #111" }} />
      </span>
    </button>
  );
}

function RenderSectionInner({ section: s, numeral, onCtaClick }) {
  const c = s.content || {};
  const variant = s.variant || "";
  if (s.section_type === "problem") {
    if (!c.body && !c.heading && !c.stat_value && !s.media) return null;
    if (variant === "stat" && (c.stat_value || c.stat_label)) {
      return (
        <div className="lp-section" data-testid="public-lp-section-problem" data-variant="stat">
          <SectionHead numeral={numeral} eyebrow="The problem" heading={c.heading} />
          <div className="lp-h1" style={{ fontSize: "clamp(48px, 5vw, 64px)", color: "var(--lp-accent, " + T.textAccent + ")", margin: 0 }}
               data-testid="public-lp-problem-stat-value">{c.stat_value}</div>
          {c.stat_label && <p className="lp-body-para" style={{ marginTop: 12 }} data-testid="public-lp-problem-stat-label">{c.stat_label}</p>}
          {c.body && <p className="lp-body-para" style={{ marginTop: 16 }}>{c.body}</p>}
        </div>);
    }
    return (<div className="lp-section" data-testid="public-lp-section-problem" data-variant="cards">
      <SectionHead numeral={numeral} eyebrow="The problem" heading={c.heading} />
      {c.body && <p className="lp-body-para">{c.body}</p>}
    </div>);
  }
  if (s.section_type === "solution") {
    if (!c.body && !c.heading && !s.media) return null;
    return (<div className="lp-section" data-testid="public-lp-section-solution" data-variant="cards">
      <SectionHead numeral={numeral} eyebrow="The solution" heading={c.heading} />
      {c.body && <p className="lp-body-para">{c.body}</p>}
    </div>);
  }
  if (s.section_type === "benefits") {
    const items = c.items || [];
    if (items.length === 0) return null;
    if (variant === "outcomes") {
      return (<div className="lp-section" data-testid="public-lp-section-benefits" data-variant="outcomes">
        <SectionHead numeral={numeral} eyebrow="Measurable outcomes" heading={c.heading} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          {items.map((b, i) => (
            <div key={i} data-testid={`public-lp-benefit-${i}`}
                 style={{ border: `1px solid ${T.hairline}`, padding: 20, borderRadius: "var(--lp-card-radius, 0px)" }}>
              <div className="lp-numeral" style={{ fontSize: 28, color: "var(--lp-accent, " + T.textAccent + ")" }}>0{i + 1}</div>
              <div style={{ marginTop: 12, fontSize: 15, color: T.textPrimary, lineHeight: 1.5 }}>{b}</div>
            </div>
          ))}
        </div>
      </div>);
    }
    return (<div className="lp-section" data-testid="public-lp-section-benefits" data-variant="icon_cards">
      <SectionHead numeral={numeral} eyebrow="What you get" heading={c.heading} />
      <div>{items.map((b, i) => <div key={i} data-testid={`public-lp-benefit-${i}`} className="lp-row">{b}</div>)}</div>
    </div>);
  }
  if (s.section_type === "proof") {
    if (!c.title && !c.description && !c.metric && !s.media) return null;
    return (<div className="lp-section" data-testid="public-lp-section-proof" data-variant="case_study">
      <SectionHead numeral={numeral} eyebrow="Proof" heading={c.heading} />
      {c.title && <h3 className="lp-h2" style={{ fontSize: 28 }} data-testid="public-lp-proof-title">{c.title}</h3>}
      {c.description && <p className="lp-body-para" style={{ marginTop: 16 }} data-testid="public-lp-proof-description">{c.description}</p>}
      {c.metric && <div className="lp-proof-metric" data-testid="public-lp-proof-metric">{c.metric}</div>}
    </div>);
  }
  if (s.section_type === "testimonials") {
    const items = c.items || [];
    if (items.length === 0) return null;
    return (<div className="lp-section" data-testid="public-lp-section-testimonials">
      <SectionHead numeral={numeral} eyebrow="What clients say" heading={c.heading} />
      <div>{items.map((t, i) => (
        <figure key={i} data-testid={`public-lp-testimonial-${i}`} className="lp-testimonial" style={{ margin: 0 }}>
          <blockquote className="lp-quote" style={{ margin: 0 }}>"{t.quote}"</blockquote>
          <figcaption className="lp-attrib"><strong>{t.name}</strong>{t.title ? ` — ${t.title}` : ""}</figcaption>
        </figure>
      ))}</div>
    </div>);
  }
  if (s.section_type === "faq") {
    const items = c.items || [];
    if (items.length === 0) return null;
    return (<div className="lp-section" data-testid="public-lp-section-faq">
      <SectionHead numeral={numeral} eyebrow="Questions" heading={c.heading} />
      <div>{items.map((qa, i) => (
        <div key={i} data-testid={`public-lp-faq-${i}`} className="lp-row">
          {qa.question && <div className="lp-faq-q">{qa.question}</div>}
          {qa.answer && <div className="lp-faq-a">{qa.answer}</div>}
        </div>))}</div>
    </div>);
  }
  if (s.section_type === "cta") {
    if (!c.headline && !c.button_text && !c.offer_text && !s.media) return null;
    const btn = c.button_text || "Get started";
    return (<div className="lp-section" data-testid="public-lp-section-cta" data-variant={variant || "standard"}>
      <SectionHead numeral={numeral} eyebrow="Ready?" />
      {variant === "offer" && c.offer_text && (
        <div data-testid="public-lp-cta-offer"
             style={{ padding: 20, border: `1px solid var(--lp-accent, ${T.textAccent})`, borderRadius: "var(--lp-card-radius, 0px)", marginBottom: 24, color: T.textPrimary, fontSize: 16 }}>
          {c.offer_text}
        </div>
      )}
      {c.headline && <h2 className="lp-cta-headline" data-testid="public-lp-cta-headline">{c.headline}</h2>}
      <div style={{ marginTop: 24 }}>
        <button type="button" data-testid="public-lp-cta-button" onClick={onCtaClick} className="lp-submit">
          <span>{btn}</span><ArrowUpRight weight="regular" size={16} color={T.textAccent} />
        </button>
      </div>
    </div>);
  }
  if (s.section_type === "how_it_works") {
    const steps = c.steps || [];
    if (steps.length === 0) return null;
    const v = variant || "numbered_steps";
    if (v === "horizontal") {
      return (<div className="lp-section" data-testid="public-lp-section-how_it_works" data-variant="horizontal">
        <SectionHead numeral={numeral} eyebrow="How it works" heading={c.heading} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
          {steps.map((st, i) => (
            <div key={i} data-testid={`public-lp-how-step-${i}`}
                 style={{ border: `1px solid ${T.hairline}`, padding: 20, borderRadius: "var(--lp-card-radius, 0px)" }}>
              <div className="lp-numeral" style={{ fontSize: 28, color: "var(--lp-accent, " + T.textAccent + ")" }}>0{i + 1}</div>
              {st.title && <div style={{ marginTop: 12, fontSize: 15, color: T.textPrimary, fontWeight: 500 }}>{st.title}</div>}
              {st.description && <div style={{ marginTop: 6, fontSize: 14, color: T.textMuted, lineHeight: 1.55 }}>{st.description}</div>}
            </div>
          ))}
        </div>
      </div>);
    }
    if (v === "vertical") {
      return (<div className="lp-section" data-testid="public-lp-section-how_it_works" data-variant="vertical">
        <SectionHead numeral={numeral} eyebrow="How it works" heading={c.heading} />
        <ol style={{ listStyle: "none", padding: 0, margin: "8px 0 0", position: "relative" }}>
          <div aria-hidden="true" style={{ position: "absolute", left: 15, top: 8, bottom: 8, width: 1, background: `var(--lp-accent, ${T.textAccent})`, opacity: 0.35 }} />
          {steps.map((st, i) => (
            <li key={i} data-testid={`public-lp-how-step-${i}`}
                style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 16, padding: "16px 0", position: "relative" }}>
              <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ width: 32, height: 32, borderRadius: 999, background: "var(--lp-bg, " + T.bg + ")", border: `1px solid var(--lp-accent, ${T.textAccent})`, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--lp-accent, " + T.textAccent + ")", fontSize: 13, fontFamily: FONT_ARAPEY, fontStyle: "italic" }}>
                  {i + 1}
                </div>
              </div>
              <div>
                {st.title && <div style={{ fontSize: 16, color: T.textPrimary, fontWeight: 500 }}>{st.title}</div>}
                {st.description && <div style={{ marginTop: 6, fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>{st.description}</div>}
              </div>
            </li>
          ))}
        </ol>
      </div>);
    }
    // numbered_steps (default)
    return (<div className="lp-section" data-testid="public-lp-section-how_it_works" data-variant="numbered_steps">
      <SectionHead numeral={numeral} eyebrow="How it works" heading={c.heading} />
      <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {steps.map((st, i) => (
          <li key={i} data-testid={`public-lp-how-step-${i}`}
              className="lp-row"
              style={{ display: "grid", gridTemplateColumns: "48px 1fr", gap: 16 }}>
            <div className="lp-numeral" style={{ fontSize: 24, color: "var(--lp-accent, " + T.textAccent + ")" }}>{i + 1}</div>
            <div>
              {st.title && <div style={{ fontSize: 16, color: T.textPrimary, fontWeight: 500 }}>{st.title}</div>}
              {st.description && <div style={{ marginTop: 4, fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>{st.description}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>);
  }
  return null;
}
